import {
  BarChart3,
  BookOpen,
  Brain,
  Eye,
  FileBarChart2,
  Gauge,
  HelpCircle,
  History,
  KeyRound,
  LayoutDashboard,
  Layers,
  Link2,
  Lock,
  MessageSquare,
  Receipt,
  Radar,
  RefreshCcw,
  Shield,
  ShieldAlert,
  Target,
  User,
  Users,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import type { DashboardRole } from '@/types/auth';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  requiredRoles?: DashboardRole[];
  /** Drives a count badge fetched in the Sidebar. */
  badge?: 'admin-pending' | 'client-unread';
  /** If set, sidebar shows a lock icon when the client does not have this product. */
  productCode?: string;
  /**
   * Short product label shown as a tinted pill in the sidebar so the underlying
   * Authmatech product is visible from the nav (e.g. "Fraud Protection" → Shield).
   * The pill colour is derived from `productCode` (shield/trust/detect tones).
   */
  productTag?: string;
}

export interface NavGroup {
  label: string | null;
  items: NavItem[];
}

const STAFF_ROLES: DashboardRole[] = [
  'AUTHMATECH_SUPER_ADMIN',
  'AUTHMATECH_ADMIN',
  'AUTHMATECH_OPERATOR',
  'AUTHMATECH_SUPPORT',
  'AUTHMATECH_FINANCE',
  'AUTHMATECH_VIEWER',
];

const ALL_CLIENT: DashboardRole[] = [
  'AUTHMATECH_CLIENT_ADMIN',
  'AUTHMATECH_CLIENT_OPERATOR',
  'AUTHMATECH_CLIENT_FINANCE',
  'AUTHMATECH_CLIENT_VIEWER',
];

const ALL_AUTHENTICATED: DashboardRole[] = [...STAFF_ROLES, ...ALL_CLIENT];

export const NAV_GROUPS: NavGroup[] = [
  // ── Admin portal ──────────────────────────────────────────────────────────
  {
    label: null,
    items: [
      { to: '/overview', label: 'Overview', icon: Gauge, requiredRoles: STAFF_ROLES },
    ],
  },
  {
    label: 'Management',
    items: [
      {
        to: '/clients',
        label: 'Clients',
        icon: Users,
        requiredRoles: [
          'AUTHMATECH_SUPER_ADMIN',
          'AUTHMATECH_ADMIN',
          'AUTHMATECH_OPERATOR',
          'AUTHMATECH_SUPPORT',
        ],
      },
      {
        to: '/verifications',
        label: 'Verifications',
        icon: History,
        requiredRoles: [
          'AUTHMATECH_SUPER_ADMIN',
          'AUTHMATECH_ADMIN',
          'AUTHMATECH_OPERATOR',
          'AUTHMATECH_SUPPORT',
        ],
      },
      {
        to: '/opportunities',
        label: 'Opportunities',
        icon: BarChart3,
        requiredRoles: [
          'AUTHMATECH_SUPER_ADMIN',
          'AUTHMATECH_ADMIN',
          'AUTHMATECH_OPERATOR',
          'AUTHMATECH_SUPPORT',
        ],
      },
    ],
  },
  {
    label: 'Analytics',
    items: [
      {
        to: '/analytics',
        label: 'Usage Analytics',
        icon: BarChart3,
        requiredRoles: [
          'AUTHMATECH_SUPER_ADMIN',
          'AUTHMATECH_ADMIN',
          'AUTHMATECH_OPERATOR',
          'AUTHMATECH_SUPPORT',
          'AUTHMATECH_FINANCE',
          'AUTHMATECH_VIEWER',
        ],
      },
      {
        to: '/reports',
        label: 'Reports',
        icon: FileBarChart2,
        requiredRoles: [
          'AUTHMATECH_SUPER_ADMIN',
          'AUTHMATECH_ADMIN',
          'AUTHMATECH_FINANCE',
          'AUTHMATECH_VIEWER',
        ],
      },
    ],
  },
  {
    label: 'Finance',
    items: [
      {
        to: '/billing',
        label: 'Billing & Invoices',
        icon: Receipt,
        requiredRoles: [
          'AUTHMATECH_SUPER_ADMIN',
          'AUTHMATECH_ADMIN',
          'AUTHMATECH_FINANCE',
        ],
      },
      {
        to: '/client-requests',
        label: 'Client Requests',
        icon: MessageSquare,
        badge: 'admin-pending',
        requiredRoles: [
          'AUTHMATECH_SUPER_ADMIN',
          'AUTHMATECH_ADMIN',
          'AUTHMATECH_SUPPORT',
          'AUTHMATECH_FINANCE',
        ],
      },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      {
        to: '/products',
        label: 'Products',
        icon: Layers,
        requiredRoles: ['AUTHMATECH_SUPER_ADMIN', 'AUTHMATECH_ADMIN'],
      },
      {
        to: '/risk-events',
        label: 'Risk Events',
        icon: ShieldAlert,
        requiredRoles: [
          'AUTHMATECH_SUPER_ADMIN',
          'AUTHMATECH_ADMIN',
          'AUTHMATECH_OPERATOR',
          'AUTHMATECH_SUPPORT',
        ],
      },
      {
        to: '/recovery-intelligence',
        label: 'Recovery Intelligence',
        icon: Brain,
        requiredRoles: [
          'AUTHMATECH_SUPER_ADMIN',
          'AUTHMATECH_ADMIN',
          'AUTHMATECH_OPERATOR',
        ],
      },
      {
        to: '/connect-intelligence',
        label: 'Connect Intelligence',
        icon: Link2,
        requiredRoles: [
          'AUTHMATECH_SUPER_ADMIN',
          'AUTHMATECH_ADMIN',
          'AUTHMATECH_OPERATOR',
        ],
      },
    ],
  },
  {
    label: 'Settings',
    items: [
      {
        to: '/security',
        label: 'Security & Access',
        icon: Shield,
        requiredRoles: ['AUTHMATECH_SUPER_ADMIN', 'AUTHMATECH_ADMIN'],
      },
    ],
  },

  // ── Client portal ─────────────────────────────────────────────────────────
  {
    label: 'My Workspace',
    items: [
      {
        to: '/portal/overview',
        label: 'Overview',
        icon: LayoutDashboard,
        requiredRoles: ALL_CLIENT,
      },
      {
        to: '/portal/verifications',
        label: 'Verification Log',
        icon: History,
        requiredRoles: ALL_CLIENT,
      },
      {
        to: '/portal/usage',
        label: 'Usage Analytics',
        icon: BarChart3,
        requiredRoles: ALL_CLIENT,
      },
    ],
  },
  {
    label: 'Fraud Intelligence',
    items: [
      {
        to: '/portal/fraud',
        label: 'Fraud Protection',
        icon: ShieldAlert,
        requiredRoles: ALL_CLIENT,
        productCode: 'SHIELD',
        productTag: 'Shield',
      },
      {
        to: '/portal/trust',
        label: 'Trust Scores',
        icon: Eye,
        requiredRoles: ALL_CLIENT,
        productCode: 'TRUST',
        productTag: 'Trust',
      },
      {
        to: '/portal/detect',
        label: 'Detect',
        icon: Radar,
        requiredRoles: ALL_CLIENT,
        productCode: 'DETECT',
        productTag: 'Detect',
      },
      {
        to: '/portal/risk-events',
        label: 'Fraud Logs',
        icon: BarChart3,
        requiredRoles: ALL_CLIENT,
      },
    ],
  },
  {
    label: 'Growth',
    items: [
      {
        to: '/portal/recovery',
        label: 'Stuck+',
        icon: RefreshCcw,
        requiredRoles: ALL_CLIENT,
        productCode: 'STUCK_PLUS',
      },
      {
        to: '/portal/connect',
        label: 'Connect',
        icon: Link2,
        requiredRoles: ALL_CLIENT,
        productCode: 'CONNECT',
      },
      {
        to: '/portal/target',
        label: 'Target',
        icon: Target,
        requiredRoles: ALL_CLIENT,
        productCode: 'SIGNAL',
      },
    ],
  },
  {
    label: 'Account',
    items: [
      {
        to: '/portal/subscription',
        label: 'Subscription',
        icon: Receipt,
        requiredRoles: ['AUTHMATECH_CLIENT_ADMIN', 'AUTHMATECH_CLIENT_FINANCE'],
      },
      {
        to: '/portal/team',
        label: 'Team & Access',
        icon: UsersRound,
        requiredRoles: ['AUTHMATECH_CLIENT_ADMIN'],
      },
      {
        to: '/portal/products',
        label: 'My Products',
        icon: Layers,
        requiredRoles: ALL_CLIENT,
      },
      {
        to: '/my-account',
        label: 'My Account',
        icon: User,
        requiredRoles: ALL_CLIENT,
      },
      {
        to: '/my-profile',
        label: 'My Profile',
        icon: User,
        requiredRoles: ALL_AUTHENTICATED,
      },
      {
        to: '/my-security',
        label: 'Security',
        icon: Lock,
        requiredRoles: ALL_AUTHENTICATED,
      },
    ],
  },
  {
    label: 'Support',
    items: [
      {
        to: '/portal/docs',
        label: 'Developer Docs',
        icon: BookOpen,
        requiredRoles: ALL_CLIENT,
      },
      {
        to: '/portal/requests',
        label: 'My Requests',
        icon: MessageSquare,
        badge: 'client-unread',
        requiredRoles: ALL_CLIENT,
      },
      {
        to: '/portal/support',
        label: 'Support',
        icon: HelpCircle,
        requiredRoles: ALL_CLIENT,
      },
      {
        to: '/settings/api-keys',
        label: 'API & Webhooks',
        icon: KeyRound,
        requiredRoles: ['AUTHMATECH_CLIENT_ADMIN', 'AUTHMATECH_CLIENT_OPERATOR'],
      },
    ],
  },
];
